import { AlertCircle, AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';
import { useTranslation } from 'next-i18next';
import type { ReactNode } from 'react';

import { bai_jamjuree } from '@/public/assets/fonts/Bai-Jamjuree';
import { cn } from '@/utils/cn';

export type AlertState = 'success' | 'error' | 'warning' | 'info';

/**
 * Most call sites keep the state in a ref/useState that starts at `''` to mean
 * "nothing to show". Accepting the empty string here lets those keep their own
 * `show` flags instead of every screen having to narrow the union first.
 */
export type AlertStateOrEmpty = AlertState | '';

interface AlertProps {
  state: AlertStateOrEmpty;
  message: ReactNode;
  /** Renders the close button. Omit for alerts that stay until the state changes. */
  onClose?: () => void;
  className?: string;
}

// One box per state: border, background tint and text share the same hue so the
// state reads without relying on the icon alone. Success/error match the values
// the dashboard notices already used; warning uses --np-bad and info --np-cyan.
//
// The tint is a background-IMAGE over the opaque --np-bg-2 base in ALERT_BASE,
// not a translucent background-color. As a toast this box floats over arbitrary
// page content, and a translucent fill let that content read straight through
// it. Painting the same tint over a solid surface keeps the intended colour
// (the measured contrast was always tint-over-#0f1215) while staying opaque —
// and because the background also extends under the border by default, the
// translucent border stops showing the page through as well.
const STATE_CLASS: Record<AlertState, string> = {
  success: 'border-[rgba(156,255,46,0.35)] bg-[linear-gradient(rgba(156,255,46,0.08),rgba(156,255,46,0.08))] text-[#9cff2e]',
  error: 'border-[#ff8a8f]/35 bg-[linear-gradient(rgba(229,72,77,0.1),rgba(229,72,77,0.1))] text-[#ff8a8f]',
  warning: 'border-[rgba(255,122,26,0.38)] bg-[linear-gradient(var(--np-bad-soft),var(--np-bad-soft))] text-[var(--np-bad-hi)]',
  info: 'border-[rgba(46,230,255,0.35)] bg-[linear-gradient(rgba(46,230,255,0.08),rgba(46,230,255,0.08))] text-[var(--np-cyan)]',
};

// The opaque surface every state paints its tint over.
const ALERT_BASE = 'flex w-full items-start gap-2.5 rounded-lg border bg-[var(--np-bg-2)] p-3 text-start';

const STATE_ICON: Record<AlertState, typeof Info> = {
  success: CheckCircle2,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

// Errors and warnings interrupt what the person is doing, so they are announced
// immediately; success and info wait for a pause in the screen reader output.
const ASSERTIVE_STATES: AlertState[] = ['error', 'warning'];

export function Alert({ state, message, onClose, className }: AlertProps) {
  // `common` is the default namespace and every page already loads it, so the
  // close label resolves without the call sites registering anything.
  const { t } = useTranslation('common');

  if (!state) return null;

  const Icon = STATE_ICON[state];
  const isAssertive = ASSERTIVE_STATES.includes(state);

  return (
    <div role={isAssertive ? 'alert' : 'status'} aria-live={isAssertive ? 'assertive' : 'polite'} className={cn(ALERT_BASE, STATE_CLASS[state], bai_jamjuree.className, className)}>
      <Icon size={16} strokeWidth={2} className='mt-0.5 shrink-0' aria-hidden='true' />
      <p className='min-w-0 flex-1 break-words text-sm font-semibold leading-[1.6]'>{message}</p>
      {onClose && (
        <button type='button' onClick={onClose} aria-label={t('a11y.close_alert')} className='-my-0.5 -me-0.5 grid size-6 shrink-0 place-items-center rounded transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current'>
          <X size={14} strokeWidth={2.5} aria-hidden='true' />
        </button>
      )}
    </div>
  );
}
